import React from 'react';
import { styled } from '@compiled/react';
import { colors } from '@atlaskit/theme';
// eslint-disable-next-line import/order
import { token } from '@atlaskit/tokens';

import { gridSize, fontSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { ff } from '@atlassian/jira-feature-flagging';
import { useIntl } from '@atlassian/jira-intl';
import { useSLAHierarchicalGoalsEnabled } from '../../../../controllers/enable-sla-hierarchical-goals';
import { EDIT_MODE } from '../../../constants';
import type { Mode } from '../../../types';
import { tableColumnNew } from '../../styled';
import messages from './messages';

type Props = {
	mode?: Mode;
};

const GoalHeaderComponent = ({ mode }: Props) => {
	const [{ isSLAHierarchicalGoalsEnabled }] = useSLAHierarchicalGoalsEnabled();
	const { formatMessage } = useIntl();

	return isSLAHierarchicalGoalsEnabled ? (
		<HeaderNewWithFF>
			{mode === EDIT_MODE && <HandleFieldNewWithFF />}
			<IssuesFieldNewWithFF>{formatMessage(messages.applyToIssues)}</IssuesFieldNewWithFF>
			<CalendarFieldNewWithFF
				id={ff('fix-jsma11y-980-issue') ? 'accessible-id-for-calendar-field' : ''}
			>
				{formatMessage(messages.calendar)}
			</CalendarFieldNewWithFF>
			<GoalFieldNewWithFF id={ff('fix-jsma11y-980-issue') ? 'accessible-id-for-time-target' : ''}>
				{formatMessage(messages.timeTarget)}
			</GoalFieldNewWithFF>
		</HeaderNewWithFF>
	) : (
		<Header>
			{mode === EDIT_MODE && <HandleField />}
			<GoalField>{formatMessage(messages.timeGoal)}</GoalField>
			<CalendarField>{formatMessage(messages.calendar)}</CalendarField>
			<IssuesField>{formatMessage(messages.jql)}</IssuesField>
		</Header>
	);
};

export const GoalHeader = GoalHeaderComponent;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Header = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	borderBottom: `2px solid ${token('color.border', colors.N40)}`,
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle', colors.N300),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	fontSize: `${fontSize}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	lineHeight: `${2.5 * gridSize}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${4 * gridSize}px`,
	margin: `${token('space.100', '8px')} 0`,
	padding: `0 ${token('space.100', '8px')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IssuesField = styled(tableColumnNew)({
	flexGrow: 2,
	flexShrink: 1,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	flexBasis: `${gridSize * 7}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const GoalField = styled(tableColumnNew)({
	flexGrow: 1,
	flexShrink: 1,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	flexBasis: `${gridSize * 8}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CalendarField = styled(tableColumnNew)({
	flexGrow: 1,
	flexShrink: 1,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	flexBasis: `${gridSize * 12}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- To migrate as part of go/ui-styling-standard
export const HandleField = styled(tableColumnNew)({
	flexGrow: 0,
	flexShrink: 1,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	flexBasis: `${gridSize * 4}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const tableColumnHeader = styled.th<{ invisible?: boolean }>({
	paddingRight: token('space.100', '8px'),
	textOverflow: 'ellipsis',
	fontWeight: 'inherit',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	visibility: (props) => (props.invisible ? 'hidden' : 'visible'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderNewWithFF = styled.tr({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	borderBottom: `2px solid ${token('color.border', colors.N40)}`,
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle', colors.N300),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	fontSize: `${fontSize}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	lineHeight: `${2.5 * gridSize}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${4 * gridSize}px`,
	margin: `${token('space.100', '8px')} 0`,
	padding: `0 ${token('space.100', '8px')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HandleFieldNewWithFF = styled(tableColumnHeader)({
	flexGrow: 0,
	flexShrink: 1,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	flexBasis: `${gridSize * 4}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IssuesFieldNewWithFF = styled(tableColumnHeader)({
	flexGrow: 2,
	flexShrink: 1,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	flexBasis: `${gridSize * 2}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const GoalFieldNewWithFF = styled(tableColumnHeader)({
	flexGrow: 1,
	flexShrink: 1,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	flexBasis: `${gridSize * 4}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CalendarFieldNewWithFF = styled(tableColumnHeader)({
	flexGrow: 1,
	flexShrink: 1,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	flexBasis: `${gridSize * 10}px`,
});
